import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWallet, api } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import CustomerBottomNav from '../../components/common/CustomerBottomNav';
import { 
  Wallet as WalletIcon, ArrowLeft,
  Plus, ArrowUpRight, ArrowDownLeft, CreditCard, CheckCircle, XCircle
} from 'lucide-react';

const paymentMethods = [
  { id: 'card', name: 'Credit/Debit Card', icon: CreditCard },
  { id: 'gcash', name: 'GCash', icon: WalletIcon },
  { id: 'maya', name: 'Maya', icon: WalletIcon },
];

export default function CustomerWallet() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { wallet, transactions, isLoading, refetch } = useWallet();
  const [showTopUpDialog, setShowTopUpDialog] = useState(false);
  const [topUpData, setTopUpData] = useState({ amount: '', payment_method: 'gcash' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymongoEnabled, setPaymongoEnabled] = useState(false);
  const [walletSettings, setWalletSettings] = useState({ min_topup_amount: 100, topup_presets: [100, 500, 1000, 2000] });

  // Check PayMongo config and wallet settings, handle return from payment
  useEffect(() => {
    const BACKEND = window.location.origin;
    fetch(`${BACKEND}/api/config/paymongo`).then(r => r.json()).then(d => {
      setPaymongoEnabled(d.enabled && d.public_key);
    }).catch(() => {});
    
    // Fetch wallet settings
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${BACKEND}/api/wallet-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).then(d => {
        if (d.min_topup_amount) setWalletSettings(d);
      }).catch(() => {});
    }

    // Cancel any pending transactions when page loads (not completed = cancelled)
    const cancelPendingTransactions = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        await fetch(`${BACKEND}/api/wallet/cancel-pending`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Refresh to show updated status
        refetch();
      } catch (error) {
        console.error('Failed to cancel pending transactions:', error);
      }
    };

    // Handle return from PayMongo checkout
    const paymentStatus = searchParams.get('payment');
    const txnId = searchParams.get('txn_id');
    if (paymentStatus === 'success' && txnId) {
      // Verify the transaction with retries
      const token = localStorage.getItem('token');
      let retries = 0;
      const maxRetries = 5;
      const retryDelay = 2000; // 2 seconds
      
      const verifyPayment = async () => {
        try {
          const response = await fetch(`${BACKEND}/api/wallet/topup/verify/${txnId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          
          if (data.status === 'completed') {
            toast.success(`Payment successful! ₱${data.amount?.toLocaleString()} added to wallet.`);
            refetch();
            return true;
          } else if (retries < maxRetries) {
            retries++;
            toast.info(`Verifying payment... (attempt ${retries}/${maxRetries})`);
            setTimeout(verifyPayment, retryDelay);
            return false;
          } else {
            toast.info('Payment is being processed. Your wallet will be updated shortly. Please refresh the page.');
            refetch();
            return false;
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          if (retries < maxRetries) {
            retries++;
            setTimeout(verifyPayment, retryDelay);
          } else {
            toast.info('Payment received. Please refresh the page to see your updated balance.');
            refetch();
          }
          return false;
        }
      };
      
      verifyPayment();
      // Clean URL
      navigate('/wallet', { replace: true });
    } else if (paymentStatus === 'cancelled') {
      const cancelTxnId = searchParams.get('txn_id');
      // Mark the transaction as cancelled in the backend
      if (cancelTxnId) {
        const token = localStorage.getItem('token');
        fetch(`${BACKEND}/api/wallet/topup/cancel/${cancelTxnId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(() => {
          refetch(); // Refresh transactions list
        }).catch(() => {});
      }
      toast.error('Payment was cancelled');
      navigate('/wallet', { replace: true });
    } else {
      // No specific payment status - cancel any pending transactions
      cancelPendingTransactions();
    }
  }, [searchParams, navigate, refetch]);

  const handleTopUp = async () => {
    const amount = parseFloat(topUpData.amount);
    const minAmount = walletSettings.min_topup_amount || 100;
    if (!amount || amount < minAmount) {
      toast.error(`Minimum top-up is ₱${minAmount}`);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const result = await api.topUpWallet({
        amount: amount,
        payment_method: topUpData.payment_method
      });
      
      // If PayMongo mode, redirect through payment page to prevent navigation issues
      if (result.data?.checkout_url && result.data?.mode === 'paymongo') {
        const transactionId = result.data.transaction_id || '';
        const checkoutUrl = encodeURIComponent(result.data.checkout_url);
        const returnUrl = encodeURIComponent('/wallet');
        navigate(`/payment?checkout_url=${checkoutUrl}&transaction_id=${transactionId}&return_url=${returnUrl}&user_type=customer`);
        return;
      }
      
      // Mock mode: instant credit
      toast.success(`₱${amount} added to wallet!`);
      setShowTopUpDialog(false);
      setTopUpData({ amount: '', payment_method: 'gcash' });
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Top-up failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-PH', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-slate-900 pt-4 pb-20 px-4 rounded-b-3xl">
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => navigate('/home')}
            className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white font-[Poppins]">Wallet</h1>
        </div>
        
        {/* Balance Card */}
        <div className="text-center">
          <p className="text-slate-400 text-sm mb-1">Available Balance</p>
          <p className="text-4xl font-bold text-white mb-4">
            ₱{wallet?.balance?.toLocaleString() || '0'}
          </p>
          <Button 
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 h-12 px-6"
            onClick={() => setShowTopUpDialog(true)}
            data-testid="topup-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Money
          </Button>
        </div>
      </header>

      <main className="px-4 -mt-8">
        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-4 shadow-lg mb-6">
          <div className="grid grid-cols-1 gap-4">
            <button 
              className="flex flex-col items-center"
              onClick={() => setShowTopUpDialog(true)}
              data-testid="topup-action-btn"
            >
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
                <ArrowDownLeft className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-xs text-slate-600">Top Up</span>
            </button>
          </div>
        </div>

        {/* Transaction History */}
        <section className="mb-24">
          <h3 className="font-bold text-slate-900 font-[Poppins] mb-4">Recent Transactions</h3>
          
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={`wallet-skeleton-${i}`} className="animate-pulse bg-stone-200 rounded-xl h-16" />
              ))}
            </div>
          ) : transactions.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {transactions.map((tx, idx) => (
                <div 
                  key={tx.id}
                  className={`p-4 flex items-center justify-between ${
                    idx > 0 ? 'border-t border-stone-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      tx.type === 'topup' ? 'bg-green-100' : 
                      tx.type === 'booking_payment' ? 'bg-red-100' :
                      tx.type === 'admin_adjustment' ? 'bg-purple-100' :
                      tx.type === 'payout' ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      {tx.type === 'topup' ? (
                        <ArrowDownLeft className="w-5 h-5 text-green-600" />
                      ) : tx.type === 'booking_payment' ? (
                        <ArrowUpRight className="w-5 h-5 text-red-600" />
                      ) : tx.type === 'admin_adjustment' ? (
                        <WalletIcon className="w-5 h-5 text-purple-600" />
                      ) : tx.type === 'payout' ? (
                        <ArrowUpRight className="w-5 h-5 text-red-600" />
                      ) : (
                        <WalletIcon className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 capitalize">
                        {tx.type === 'topup' ? 'Wallet Top-up' :
                         tx.type === 'booking_payment' ? 'Booking Payment' :
                         tx.type === 'admin_adjustment' ? 'HandyBayani Adjustment' :
                         tx.type === 'payout' ? 'Withdrawal' : tx.type.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-slate-500">{formatDate(tx.created_at)}</p>
                      {tx.description && <p className="text-xs text-slate-400">{tx.description}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${
                      tx.amount >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {tx.amount >= 0 ? '+' : '-'}₱{Math.abs(tx.amount).toLocaleString()}
                    </p>
                    <p className={`text-xs capitalize ${
                      tx.status === 'completed' ? 'text-green-600' : 
                      tx.status === 'cancelled' || tx.status === 'failed' || tx.status === 'expired' ? 'text-red-600' :
                      'text-amber-600'
                    }`}>
                      {tx.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-stone-50 rounded-2xl">
              <WalletIcon className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500">No transactions yet</p>
            </div>
          )}
        </section>
      </main>

      {/* Bottom Navigation */}
      <CustomerBottomNav />

      {/* Top Up Dialog */}
      <Dialog open={showTopUpDialog} onOpenChange={setShowTopUpDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Money to Wallet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Amount</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₱</span>
                <Input
                  type="number"
                  placeholder="0"
                  value={topUpData.amount}
                  onChange={(e) => setTopUpData(prev => ({ ...prev, amount: e.target.value }))}
                  className="h-14 pl-8 text-2xl font-bold"
                  data-testid="topup-amount-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Minimum: ₱{walletSettings.min_topup_amount || 100}</p>
            </div>
            
            {/* Quick Amounts */}
            <div className="flex gap-2">
              {(walletSettings.topup_presets || [100, 500, 1000, 2000]).map(amount => (
                <button
                  key={amount}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                    topUpData.amount === amount.toString()
                      ? 'bg-slate-900 text-white'
                      : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
                  }`}
                  onClick={() => setTopUpData(prev => ({ ...prev, amount: amount.toString() }))}
                >
                  ₱{amount}
                </button>
              ))}
            </div>
            
            <div>
              <Label>Payment Method</Label>
              <div className="space-y-2 mt-2">
                {paymentMethods.map(method => (
                  <label
                    key={method.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer ${
                      topUpData.payment_method === method.id
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value={method.id}
                      checked={topUpData.payment_method === method.id}
                      onChange={(e) => setTopUpData(prev => ({ ...prev, payment_method: e.target.value }))}
                      className="hidden"
                    />
                    <method.icon className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-900">{method.name}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <Button 
              className="w-full h-12 bg-slate-900 hover:bg-slate-800"
              onClick={handleTopUp}
              disabled={isSubmitting}
              data-testid="confirm-topup-btn"
            >
              {isSubmitting ? 'Processing...' : paymongoEnabled ? 'Proceed to Payment' : 'Add Money'}
            </Button>
            
            {!paymongoEnabled && (
              <p className="text-xs text-center text-slate-500">
                Payment processing is in demo mode
              </p>
            )}
            {paymongoEnabled && (
              <p className="text-xs text-center text-slate-500">
                You will be redirected to complete payment via GCash, Maya, or Card
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
